import { useCallback, useMemo, useRef } from 'react';

import { useTheme } from '@mui/material';

import useBaseChartOptions from '@/components/Chart/hooks/options/useBaseChartOptions';
import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useSpotTooltipFormatter from '@/components/Chart/hooks/tooltip/useSpotTooltipFormatter';
import { createChartAppearance } from '@/components/Chart/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { createLastPriceLabel } from '@/components/Chart/utils/createLastPriceLabel';
import { applySeriesVisibility } from '@/components/Chart/utils/seriesVisibility';
import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import { Permission } from '@/entities/permissions';
import usePermission from '@/shared/hooks/usePermission';

import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig
} from './useChartFunctions';

import type Highcharts from 'highcharts/highstock';

const useRegularOptions = (height?: number, isDhChart = false) => {
  const createTooltipFormatter = useSpotTooltipFormatter(isDhChart ? 'Alias' : undefined);
  const theme = useTheme();
  const { hasPermission } = usePermission();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, theme).tooltip,
    [createTooltipFormatter, theme]
  );

  const { createBaseChartOptions } = useBaseChartOptions(
    height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT
  );

  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);

  const appearance = createChartAppearance(theme, 'regular');
  const { aspectRatio } = getWatermarkConfig(appearance.watermarkName);

  const getWatermarkAttributes = (chart: Highcharts.Chart) => {
    return getStandardWatermarkAttributes({
      chart,
      aspectRatio,
      useSmallWatermark
    });
  };

  const { events, exportEvents } = createEventOptions(
    watermarkImage,
    getWatermarkAttributes,
    appearance.watermarkName
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const visibleSeries = applySeriesVisibility(chartSeries, hiddenSeriesIds) ?? chartSeries;
      const baseOptions = createBaseChartOptions(visibleSeries, appearance);

      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, events },
        plotOptions: {
          ...baseOptions.plotOptions,
          series: {
            ...baseOptions.plotOptions?.series,
            dataLabels: {
              position: 'right',
              enabled: true,
              useHTML: true,
              formatter(): string | undefined {
                return createLastPriceLabel(this, theme.palette.gx.mainBackground);
              }
            }
          }
        },
        exporting: {
          ...baseOptions.exporting,
          chartOptions: {
            ...baseOptions.exporting?.chartOptions,
            chart: { events: exportEvents, backgroundColor: appearance.backgroundColor },
            plotOptions: { series: { dataLabels: { enabled: false } } }
          },
          sourceWidth: undefined,
          sourceHeight: undefined,
          buttons: undefined
        },
        stockTools: { gui: { enabled: false } },
        tooltip,
        series: visibleSeries
      };
    },
    [
      appearance,
      createBaseChartOptions,
      theme.palette.gx.mainBackground,
      tooltip,
      events,
      exportEvents
    ]
  );

  return { createOptions };
};

export default useRegularOptions;
